import { failure, request, success } from "@/utils/constants";
import { eventConstants } from "../actions/constants/eventConstants";
import { localData } from "@/services/helper";

interface EventWorkflow {
  step: number;
  mode: "view" | "edit";
  permissions: {
    canEdit: boolean;
    canAssign: boolean;
    canApproveReject: boolean;
    canViewAllAttendance: boolean;
    canMarkSelfAttendance: boolean;
    canUploadMedia: boolean;
    canClose: boolean;
  };
}

interface EventLoadedFlags {
  event: boolean;
  assignment: boolean;
  attendance: boolean;
  media: boolean;
  finance: boolean;
  expenses: boolean;
  financial: boolean;
}

interface EventLoadingFlags {
  event: boolean;
  assignment: boolean;
  attendance: boolean;
  media: boolean;
  finance: boolean;
  expenses: boolean;
  financial: boolean;
}

interface EventEntity {
  data: any;
  status: string;
  workflow: EventWorkflow;
  loaded: EventLoadedFlags;
  loading: EventLoadingFlags;
  error: any;
  deepLinkContext?: {
    assignmentId?: string;
    action?: string;
    fromDeepLink?: boolean;
  };
}

interface NormalizedEventState {
  currentEventId: string | null;
  entities: {
    [eventId: string]: EventEntity;
  };
}

interface CreateEventState {
  loading: boolean;
  error: any;
  success: boolean;
  event?: any;
}

const createEventInitialState: CreateEventState = {
  loading: false,
  error: null,
  success: false,
  event: undefined,
};

export const createEventReducer = (state = createEventInitialState, action: any): CreateEventState => {
  switch (action.type) {
    case eventConstants.createEventRequest:
      return {
        ...state,
        loading: true,
        error: null,
        success: false,
      };
    case eventConstants.createEventSuccess:
      return {
        ...state,
        loading: false,
        error: null,
        success: true,
        event: action.payload,
      };
    case eventConstants.createEventFailure:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false,
      };
    case eventConstants.clearCreateEventState:
      return createEventInitialState;
    default:
      return state;
  }
};

interface EventListState {
  events: {
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
  loading: boolean;
  error: any;
}

const eventListInitialState: EventListState = {
  events: null,
  loading: false,
  error: null,
};

export const eventListReducer = (state = eventListInitialState, action: any): EventListState => {
  switch (action.type) {
    case eventConstants.listEventsRequest:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case eventConstants.listEventsSuccess:
      return {
        ...state,
        loading: false,
        events: action.payload,
        error: null,
      };
    case eventConstants.listEventsFailure:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};




export const viewEventReducer = (state = {}, action: any) => {
    switch (action.type) {
        case eventConstants.viewEventsRequest:
            return {
                ...request,
            };
        case eventConstants.viewEventsSuccess:
            return {
                ...success,
                eventDetails: action.payload,
            };
        case eventConstants.viewEventsFailure:
            return {
                ...failure,
            };
        default:
            return state;
    }
};

export const updateEventReducer = (state = {}, action: any) => {
    switch (action.type) {
        case eventConstants.updateEventsRequest:
            return {
                ...request,
                loading: true,
            };
        case eventConstants.updateEventsSuccess:
            return {
                ...success,
                loading: false,
                eventDetails: action.payload,
                isSuccess: true,
            };
        case eventConstants.updateEventsFailure:
            return {
                ...failure,
                loading: false,
            };
        case eventConstants.updateEventsRequiresConfirmation:
            return {
                loading: false,
                success: false,
                isSuccess: false,
                requiresConfirmation: true,
                impactSummary: action.payload?.impactSummary || [],
                impactKind: action.payload?.impactKind || "service-removed",
                confirmationMessage: action.payload?.message || "",
                pendingPayload: action.payload?.pendingPayload || null,
            };
        case eventConstants.clearUpdateEventConfirmation:
            return {
                ...state,
                requiresConfirmation: false,
                impactSummary: [],
                impactKind: undefined,
                confirmationMessage: "",
                pendingPayload: null,
            };
        case eventConstants.clearUpdateEventState:
            return {
                isSuccess: false,
            };
        default:
            return state;
    }
};

export const assignMemberToEventsReducer = (state = {}, action: any) =>{
  switch (action.type) {
    case eventConstants.assignMemberToEventsRequest:
      return {
        ...request,
        loading: true,
      };
    case eventConstants.assignMemberToEventsSuccess:
      return {
        ...success,
        events: action.payload,
      };
    case eventConstants.assignMemberToEventsFailure:
      return {
        ...failure,
        error: action.payload,
      };
    case 'CLEAR_ASSIGN_MEMBER_STATE':
      return {};
    default:
      return state;
  }
}

export const closeEventReducer = (state = {}, action: any) => {
  switch (action.type) {
    case eventConstants.closeEventRequest:
      return {
        ...request,
        loading: true,
      };
    case eventConstants.closeEventSuccess:
      return {
        ...success,
        success: true,
        event: action.payload,
      };
    case eventConstants.closeEventFailure:
      return {
        ...failure,
        error: action.payload,
      };
    case eventConstants.clearCloseEventState:
      return {};
    default:
      return state;
  }
};

export const getEventsWithEnquiriesReducer = (state = {}, action: any) => {
    switch (action.type) {
        case eventConstants.getEventsWithEnquiriesRequest:
            return { ...request };
        case eventConstants.getEventsWithEnquiriesSuccess:
            return { ...success, events: action.payload };
        case eventConstants.getEventsWithEnquiriesFailure:
            return { ...failure };
        default:
            return state;
    }
};

export const updateEventStageReducer = (state = {}, action: any) => {
    switch (action.type) {
        case eventConstants.updateEventStageRequest:
            return { ...request };
        case eventConstants.updateEventStageSuccess:
            return { ...success, event: action.payload };
        case eventConstants.updateEventStageFailure:
            return { ...failure, error: action.payload };
        case eventConstants.clearUpdateEventStageState:
            return {};
        default:
            return state;
    }
};

const createDefaultWorkflow = (): EventWorkflow => ({
  step: 0,
  mode: "view",
  permissions: {
    canEdit: false,
    canAssign: false,
    canApproveReject: false,
    canViewAllAttendance: false,
    canMarkSelfAttendance: false,
    canUploadMedia: false,
    canClose: false,
  },
});

const createDefaultLoadedFlags = (): EventLoadedFlags => ({
  event: false,
  assignment: false,
  attendance: false,
  media: false,
  finance: false,
  expenses: false,
  financial: false,
});

const createDefaultLoadingFlags = (): EventLoadingFlags => ({
  event: false,
  assignment: false,
  attendance: false,
  media: false,
  finance: false,
  expenses: false,
  financial: false,
});

const createDefaultEventEntity = (): EventEntity => ({
  data: null,
  status: "",
  workflow: createDefaultWorkflow(),
  loaded: createDefaultLoadedFlags(),
  loading: createDefaultLoadingFlags(),
  error: null,
});

const getSavedEventId = (): string | null => {
  try {
    return localStorage.getItem("currentEventId");
  } catch {
    return null;
  }
};

const normalizedEventInitialState: NormalizedEventState = {
  currentEventId: getSavedEventId(),
  entities: {},
};

export const normalizedEventReducer = (
  state = normalizedEventInitialState,
  action: any
): NormalizedEventState => {
  switch (action.type) {
    
    case "SET_CURRENT_EVENT_ID": {
      const eventId = action.payload;
      
      if (eventId) {
        localStorage.setItem("currentEventId", eventId);
      }
      
      return {
        ...state,
        currentEventId: eventId,
        entities: state.entities[eventId]
          ? state.entities
          : {
              ...state.entities,
              [eventId]: createDefaultEventEntity(),
            },
      };
    }

    case "CLEAR_CURRENT_EVENT_ID":
      localData.remove("currentEventId");
      
      return {
        ...state,
        currentEventId: null,
      };


    case "SET_EVENT_DATA": {
      const { eventId, data } = action.payload;
      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...(state.entities[eventId] || createDefaultEventEntity()),
            data,
          },
        },
      };
    }

    case "UPDATE_EVENT_DATA": {
      const { eventId, updates } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            data: {
              ...(existingEntity.data || {}),
              ...updates,
            },
          },
        },
      };
    }

    case "SET_FINANCE_DATA": {
      const { eventId, financeData } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            data: {
              ...(existingEntity.data || {}),
              finance: financeData,
            },
          },
        },
      };
    }

    case "SET_EXPENSE_DATA": {
      const { eventId, expenseData } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            data: {
              ...(existingEntity.data || {}),
              expenses: expenseData,
            },
          },
        },
      };
    }

    case "SET_FINANCIAL_DATA": {
      const { eventId, financialData } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            data: {
              ...(existingEntity.data || {}),
              financial: financialData,
            },
          },
        },
      };
    }

    case "SET_WORKFLOW": {
      const { eventId, workflow } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            workflow: {
              ...existingEntity.workflow,
              ...workflow,
            },
          },
        },
      };
    }

    case "UPDATE_WORKFLOW_STEP": {
      const { eventId, step } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            workflow: {
              ...existingEntity.workflow,
              step,
            },
          },
        },
      };
    }

    case "UPDATE_WORKFLOW_PERMISSIONS": {
      const { eventId, permissions } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            workflow: {
              ...existingEntity.workflow,
              permissions: {
                ...existingEntity.workflow.permissions,
                ...permissions,
              },
            },
          },
        },
      };
    }

    case "SET_LOADED_FLAG": {
      const { eventId, flagName, value } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            loaded: {
              ...existingEntity.loaded,
              [flagName]: value,
            },
          },
        },
      };
    }

    case "SET_MULTIPLE_LOADED_FLAGS": {
      const { eventId, flags } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            loaded: {
              ...existingEntity.loaded,
              ...flags,
            },
          },
        },
      };
    }

    case "SET_LOADING_FLAG": {
      const { eventId, flagName, value } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            loading: {
              ...existingEntity.loading,
              [flagName]: value,
            },
          },
        },
      };
    }

    case "SET_MULTIPLE_LOADING_FLAGS": {
      const { eventId, flags } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            loading: {
              ...existingEntity.loading,
              ...flags,
            },
          },
        },
      };
    }

    case "SET_EVENT_ERROR": {
      const { eventId, error } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            error,
          },
        },
      };
    }

    case "CLEAR_EVENT_ERROR": {
      const { eventId } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            error: null,
          },
        },
      };
    }

    case "UPDATE_ASSIGNMENT_IN_STORE": {
      const { eventId, assignment } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            data: {
              ...existingEntity.data,
              photographerAssignments: assignment,
            },
            loaded: {
              ...existingEntity.loaded,
              assignment: true,
            },
          },
        },
      };
    }

    case "INITIALIZE_EVENT_ENTITY": {
      const { eventId, initialData } = action.payload;
      
      if (state.entities[eventId]) {
        return state;
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...createDefaultEventEntity(),
            data: initialData || null,
          },
        },
      };
    }

    case "REMOVE_EVENT_ENTITY": {
      const { eventId } = action.payload;
      const { [eventId]: removed, ...remainingEntities } = state.entities;

      return {
        ...state,
        currentEventId: state.currentEventId === eventId ? null : state.currentEventId,
        entities: remainingEntities,
      };
    }

    case "CLEAR_ALL_EVENT_ENTITIES":
      return normalizedEventInitialState;

    case "SET_DEEP_LINK_CONTEXT": {
      const { eventId, context } = action.payload;
      const existingEntity = state.entities[eventId] || createDefaultEventEntity();

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: {
            ...existingEntity,
            deepLinkContext: context,
          },
        },
      };
    }

    case "CLEAR_DEEP_LINK_CONTEXT": {
      const { eventId } = action.payload;
      const existingEntity = state.entities[eventId];
      if (!existingEntity) return state;

      const { deepLinkContext, ...entityWithoutContext } = existingEntity;

      return {
        ...state,
        entities: {
          ...state.entities,
          [eventId]: entityWithoutContext,
        },
      };
    }

    case eventConstants.viewEventsRequest: {
      const eventId = action.payload?.id;
      if (eventId && state.entities[eventId]) {
        return {
          ...state,
          entities: {
            ...state.entities,
            [eventId]: {
              ...state.entities[eventId],
              loading: {
                ...state.entities[eventId].loading,
                event: true,
              },
            },
          },
        };
      }
      return state;
    }

    case eventConstants.viewEventsSuccess: {
      const eventData = action.payload?.event;
      if (eventData && eventData._id && state.entities[eventData._id]) {
        return {
          ...state,
          entities: {
            ...state.entities,
            [eventData._id]: {
              ...state.entities[eventData._id],
              data: action.payload,
              status: eventData.status || state.entities[eventData._id].status,
              loaded: {
                ...state.entities[eventData._id].loaded,
                event: true,
                assignment: true,
              },
              loading: {
                ...state.entities[eventData._id].loading,
                event: false,
                assignment: false,
              },
            },
          },
        };
      }
      return state;
    }

    case eventConstants.assignMemberToEventsSuccess: {
      const eventData = action.payload?.event;
      if (eventData && eventData._id && state.entities[eventData._id]) {
        return {
          ...state,
          entities: {
            ...state.entities,
            [eventData._id]: {
              ...state.entities[eventData._id],
              data: {
                ...state.entities[eventData._id].data,
                event: eventData,
                photographerAssignments: action.payload?.photographerAssignments,
              },
              status: eventData.status || state.entities[eventData._id].status,
              loaded: {
                ...state.entities[eventData._id].loaded,
                assignment: true,
              },
              loading: {
                ...state.entities[eventData._id].loading,
                assignment: false,
              },
            },
          },
        };
      }
      return state;
    }

    default:
      return state;
  }
};

export const eventSelectors = {
  getCurrentEventId: (state: any): string | null =>
    state.normalizedEvent?.currentEventId || null,

  getCurrentEntity: (state: any): EventEntity | null => {
    const eventId = state.normalizedEvent?.currentEventId;
    return eventId ? state.normalizedEvent?.entities[eventId] || null : null;
  },

  getEntityById: (state: any, eventId: string | any): EventEntity | null => 
    state.normalizedEvent?.entities[eventId] || null,

  getEntityData: (state: any, eventId: string): any =>
    state.normalizedEvent?.entities[eventId]?.data || null,

  getEntityWorkflow: (state: any, eventId: string): EventWorkflow | null =>
    state.normalizedEvent?.entities[eventId]?.workflow || null,

  getEntityLoadedFlags: (state: any, eventId: string): EventLoadedFlags | null =>
    state.normalizedEvent?.entities[eventId]?.loaded || null,

  getEntityLoadingFlags: (state: any, eventId: string): EventLoadingFlags | null =>
    state.normalizedEvent?.entities[eventId]?.loading || null,

  isEntityLoaded: (state: any, eventId: string, flagName: keyof EventLoadedFlags): boolean =>
    state.normalizedEvent?.entities[eventId]?.loaded[flagName] || false,

  isEntityLoading: (state: any, eventId: string, flagName: keyof EventLoadingFlags): boolean =>
    state.normalizedEvent?.entities[eventId]?.loading[flagName] || false,

  getDeepLinkContext: (state: any, eventId: string) =>
    state.normalizedEvent?.entities[eventId]?.deepLinkContext || null,
};
